"use client";

import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import {
  type AppLocale,
  getEnabledLocales,
  localeConfigs,
  locales,
} from "@/i18n/locales";
import { type ChangeEvent } from "react";

export function LanguageSwitcher({
  showAllLocales = false,
}: {
  /** If true, shows all registered locales; otherwise only shows published/enabled ones. */
  showAllLocales?: boolean;
}) {
  const currentLocale = useLocale() as AppLocale;
  const pathname = usePathname();
  const router = useRouter();

  const availableLocales = showAllLocales
    ? (locales as readonly AppLocale[])
    : getEnabledLocales();

  // If only one locale is enabled and we aren't showing preview locales, don't clutter the header
  if (availableLocales.length <= 1 && !showAllLocales) {
    return null;
  }

  function handleLocaleChange(e: ChangeEvent<HTMLSelectElement>) {
    const nextLocale = e.target.value as AppLocale;
    if (nextLocale === currentLocale) return;

    // next-intl's router automatically maps the unlocalized pathname to the target locale slug
    router.replace(pathname, { locale: nextLocale });
  }

  return (
    <div className="relative inline-flex items-center">
      <label htmlFor="language-switcher" className="sr-only">
        Choose Language
      </label>
      <div className="pointer-events-none absolute left-2.5 text-slate-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      </div>
      <select
        id="language-switcher"
        value={currentLocale}
        onChange={handleLocaleChange}
        className="appearance-none rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-7 text-xs font-medium text-slate-700 shadow-sm transition hover:border-slate-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {availableLocales.map((code) => {
          const cfg = localeConfigs[code];
          return (
            <option key={code} value={code}>
              {cfg.nativeName}
            </option>
          );
        })}
      </select>
      <div className="pointer-events-none absolute right-2 text-slate-400">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
}

